import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { usePermissions, useTranslate } from "react-admin";
import { Navigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AnalyticsFilterBar } from "../analytics/AnalyticsFilterBar";
import { useAnalyticsData } from "../analytics/AnalyticsProvider";
import { useAnalyticsFilters } from "../analytics/useAnalyticsFilters";
import {
  AnalyticsGroupBy,
  CHART_PALETTE,
  DailyRow,
  deriveFlavourFromAppId,
  formatAnalyticsGroupKey,
  formatDateLabel,
  generateKeys,
  getAnalyticsGroupKey,
  Granularity,
  KpiKey,
  truncDate,
} from "../analytics/utils";
import { ResourceName } from "../constants/resources";
import { space } from "../theme/designTokens";

const MESSAGES_EXCLUDED = new Set(["senior-games", "senior-b2bBasic"]);
const NOTES_EXCLUDED = new Set(["senior-games", "senior-b2bBasic", "family", "senior-b2c"]);

const isExcludedGroupKey = (groupKey: string, groupBy: AnalyticsGroupBy, excludedFlavours: Set<string>) =>
  excludedFlavours.has(groupBy === AnalyticsGroupBy.AppId ? deriveFlavourFromAppId(groupKey) : groupKey);

const BarTrendChart = ({
  rows,
  groupBy,
  groupKeys,
  metric,
  label,
  dateFrom,
  dateTo,
  granularity,
}: {
  rows: DailyRow[];
  groupBy: AnalyticsGroupBy;
  groupKeys: string[];
  metric: KpiKey;
  label: string;
  dateFrom: string;
  dateTo: string;
  granularity: Granularity;
}) => {
  const data = useMemo(() => {
    const byKey = new Map<string, Record<string, number>>();
    for (const r of rows) {
      const key = truncDate(r.date, granularity);
      const entry = byKey.get(key) ?? {};
      const groupKey = getAnalyticsGroupKey(r.appId, groupBy);
      entry[groupKey] = (entry[groupKey] ?? 0) + r[metric];
      byKey.set(key, entry);
    }
    return generateKeys(dateFrom, dateTo, granularity).map(date => ({
      date,
      ...(byKey.get(date) ?? {}),
    }));
  }, [rows, metric, granularity, dateFrom, dateTo, groupBy]);

  const [hoveredFlavour, setHoveredFlavour] = useState<string | null>(null);
  const handleMouseLeave = useCallback(() => setHoveredFlavour(null), []);

  return (
    <Box sx={{ mb: space.lg }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 0 }} onMouseLeave={handleMouseLeave}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={v => formatDateLabel(v, granularity)} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip labelFormatter={v => formatDateLabel(String(v), granularity)} />
          <Legend onMouseEnter={e => setHoveredFlavour(e.dataKey as string)} onMouseLeave={handleMouseLeave} />
          {groupKeys.map((f, i) => (
            <Bar
              key={f}
              dataKey={f}
              name={formatAnalyticsGroupKey(f, groupBy)}
              fill={CHART_PALETTE[i % CHART_PALETTE.length]}
              fillOpacity={hoveredFlavour === null || hoveredFlavour === f ? 1 : 0.15}
              onMouseEnter={() => setHoveredFlavour(f)}
              onMouseLeave={handleMouseLeave}
              style={{ cursor: "pointer" }}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const CumulativeChart = ({
  rows,
  groupBy,
  groupKeys,
  metric,
  label,
  dateFrom,
  dateTo,
  granularity,
}: {
  rows: DailyRow[];
  groupBy: AnalyticsGroupBy;
  groupKeys: string[];
  metric: KpiKey;
  label: string;
  dateFrom: string;
  dateTo: string;
  granularity: Granularity;
}) => {
  const data = useMemo(() => {
    const byKey = new Map<string, Record<string, number>>();
    for (const r of rows) {
      const key = truncDate(r.date, granularity);
      const entry = byKey.get(key) ?? {};
      const groupKey = getAnalyticsGroupKey(r.appId, groupBy);
      entry[groupKey] = (entry[groupKey] ?? 0) + r[metric];
      byKey.set(key, entry);
    }
    const allKeys = generateKeys(dateFrom, dateTo, granularity);
    const totals: Record<string, number> = {};
    return allKeys.map(date => {
      const values = byKey.get(date) ?? {};
      const point: Record<string, string | number> = { date };
      for (const f of groupKeys) {
        totals[f] = (totals[f] ?? 0) + (values[f] ?? 0);
        point[f] = totals[f];
      }
      return point;
    });
  }, [rows, groupBy, groupKeys, metric, granularity, dateFrom, dateTo]);

  const [hoveredFlavour, setHoveredFlavour] = useState<string | null>(null);
  const handleMouseLeave = useCallback(() => setHoveredFlavour(null), []);

  return (
    <Box sx={{ mb: space.lg }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 0 }} onMouseLeave={handleMouseLeave}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={v => formatDateLabel(v, granularity)} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip labelFormatter={v => formatDateLabel(String(v), granularity)} />
          <Legend onMouseEnter={e => setHoveredFlavour(e.dataKey as string)} onMouseLeave={handleMouseLeave} />
          {groupKeys.map((f, i) => (
            <Line
              key={f}
              type="monotone"
              dataKey={f}
              name={formatAnalyticsGroupKey(f, groupBy)}
              stroke={CHART_PALETTE[i % CHART_PALETTE.length]}
              strokeWidth={hoveredFlavour === f ? 3 : 2}
              strokeOpacity={hoveredFlavour === null || hoveredFlavour === f ? 1 : 0.15}
              dot={false}
              isAnimationActive={false}
              onMouseEnter={() => setHoveredFlavour(f)}
              onMouseLeave={handleMouseLeave}
              style={{ cursor: "pointer" }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const AnalyticsFeatures = () => {
  const { permissions, isPending } = usePermissions();
  const t = useTranslate();
  const { dailyRows, loading, error } = useAnalyticsData();
  const filters = useAnalyticsFilters();
  const [groupBy, setGroupBy] = useState<AnalyticsGroupBy>(AnalyticsGroupBy.AppId);

  const filteredRows = useMemo(
    () =>
      dailyRows
        .filter(r => r.date.slice(0, 10) >= filters.deferredFrom && r.date.slice(0, 10) <= filters.deferredTo)
        .sort((a, b) => b.date.localeCompare(a.date) || a.appId.localeCompare(b.appId)),
    [dailyRows, filters.deferredFrom, filters.deferredTo]
  );

  const latestDate = filteredRows[0]?.date;
  const chartGroupKeys = useMemo(
    () => Array.from(new Set(filteredRows.map(row => getAnalyticsGroupKey(row.appId, groupBy)))).sort(),
    [filteredRows, groupBy]
  );
  const messagesGroupKeys = useMemo(
    () => chartGroupKeys.filter(groupKey => !isExcludedGroupKey(groupKey, groupBy, MESSAGES_EXCLUDED)),
    [chartGroupKeys, groupBy]
  );
  const notesGroupKeys = useMemo(
    () => chartGroupKeys.filter(groupKey => !isExcludedGroupKey(groupKey, groupBy, NOTES_EXCLUDED)),
    [chartGroupKeys, groupBy]
  );

  if (!isPending && !permissions?.includes(ResourceName.Analytics)) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ p: 4 }}>
      <AnalyticsFilterBar
        interval={filters.interval}
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        granularity={filters.granularity}
        latestDate={latestDate}
        onIntervalChange={filters.handleIntervalChange}
        onDateFromChange={filters.setDateFrom}
        onDateToChange={filters.setDateTo}
        onGranularityChange={filters.setGranularity}
      />
      <FormControl size="small" sx={{ minWidth: 180, mt: space.sm, mb: space.lg }}>
        <InputLabel id="analytics-features-group-by-label">{t("analytics.groupBy")}</InputLabel>
        <Select
          labelId="analytics-features-group-by-label"
          value={groupBy}
          label={t("analytics.groupBy")}
          onChange={(event: SelectChangeEvent<AnalyticsGroupBy>) => setGroupBy(event.target.value as AnalyticsGroupBy)}
        >
          <MenuItem value={AnalyticsGroupBy.AppId}>{t("analytics.groupByOption.appId")}</MenuItem>
          <MenuItem value={AnalyticsGroupBy.Flavour}>{t("analytics.groupByOption.flavour")}</MenuItem>
        </Select>
      </FormControl>

      {loading && <CircularProgress sx={{ my: space.lg }} />}
      {error && (
        <Typography color="error" sx={{ mb: space.md }}>
          {t("analytics.loadError")}
        </Typography>
      )}

      {filters.deferredFrom && filters.deferredTo && (
        <>
          <BarTrendChart
            rows={filteredRows}
            groupBy={groupBy}
            groupKeys={messagesGroupKeys}
            metric="messagesSent"
            label={t("analytics.kpi.messagesSent")}
            dateFrom={filters.deferredFrom}
            dateTo={filters.deferredTo}
            granularity={filters.granularity}
          />
          <BarTrendChart
            rows={filteredRows}
            groupBy={groupBy}
            groupKeys={messagesGroupKeys}
            metric="picturesSent"
            label={t("analytics.kpi.picturesSent")}
            dateFrom={filters.deferredFrom}
            dateTo={filters.deferredTo}
            granularity={filters.granularity}
          />
          <BarTrendChart
            rows={filteredRows}
            groupBy={groupBy}
            groupKeys={notesGroupKeys}
            metric="notesCreated"
            label={t("analytics.kpi.notesCreated")}
            dateFrom={filters.deferredFrom}
            dateTo={filters.deferredTo}
            granularity={filters.granularity}
          />
          <CumulativeChart
            rows={filteredRows}
            groupBy={groupBy}
            groupKeys={notesGroupKeys}
            metric="b2BSeniorsCreated"
            label={t("analytics.kpi.b2BSeniorsTotal")}
            dateFrom={filters.deferredFrom}
            dateTo={filters.deferredTo}
            granularity={filters.granularity}
          />
        </>
      )}
    </Box>
  );
};
